// Client-side mirror of services/api/src/modules/production/runs.ts's CLOSE-time
// backflush computation: every recipe consumable line without a matching
// consumable already recorded on the run gets a BACKFLUSHED default line at CLOSE
// (`qtyPerOutputTonne x KG-denominated output tonnes`, ruling R6 — runs.ts's
// `/close` handler filters the tonnage sum to outputs whose `products.base_uom`
// is KG, because an EACH/L output's `qtyKg` column holds a unit count, not a
// mass), all booked from one operator-chosen location. This lets the operator
// see, before closing, exactly which consumables the server will auto-add and
// how much — so the "consumables (backflushed from recipe, editable)" step
// (BUILD_SPEC.md §8.3) actually shows real numbers instead of an opaque "some
// will be added" note. `totalOutputWetTonnes` sums WET `qtyKg` (not dry) over
// the KG-scoped lines to match the server's own `Decimal` reduction verbatim.
// When the KG basis is zero (e.g. a KG->EACH packing run), the server does NOT
// backflush zero — it refuses to close with a BadRequestError directing the
// operator to record the consumables as ACTUAL lines instead; the preview
// mirrors that as a null estimate (see `PendingConsumable.estimatedQty`).
import type { BaseUom } from '@zebra/core';
import type { RecipeDto, RunConsumableLineDto, RunOutputLineDto } from './types';

/** WET output tonnage over KG-baseUom lines only — the backflush multiplier
 * basis, exactly as runs.ts's `/close` computes `totalOutputTonnes` (R6). */
export function totalOutputWetTonnes(
  outputs: RunOutputLineDto[],
  baseUomByProductId: ReadonlyMap<string, { baseUom: BaseUom }>,
): number {
  return (
    outputs.reduce(
      (sum, o) => (baseUomByProductId.get(o.productId)?.baseUom === 'KG' ? sum + Number(o.qtyKg) : sum),
      0,
    ) / 1000
  );
}

export interface PendingConsumable {
  productId: string;
  qtyPerOutputTonne: string;
  /** null = no backflush estimate exists. Either the run's KG-denominated output
   * tonnage is zero — in which case the server will REFUSE to close until these
   * are recorded as ACTUAL lines (runs.ts `/close`'s "Cannot backflush recipe
   * consumable(s) …: this run recorded zero KG-denominated output mass" error) —
   * or the caller didn't supply baseUom info (count-only callers). */
  estimatedQty: string | null;
}

/** Recipe consumables not yet recorded on the run (by product id) — these are the
 * ones `/close` will auto-add as BACKFLUSHED lines. Membership is by productId
 * only (independent of tonnage), so callers that only need the COUNT of pending
 * lines (RecordShiftWizard, the run detail route) may omit `baseUomByProductId`;
 * callers rendering `estimatedQty` must supply it, or every estimate is null. */
export function pendingRecipeConsumables(
  recipe: RecipeDto | undefined,
  outputs: RunOutputLineDto[],
  recorded: RunConsumableLineDto[],
  baseUomByProductId?: ReadonlyMap<string, { baseUom: BaseUom }>,
): PendingConsumable[] {
  if (!recipe) return [];
  const recordedIds = new Set(recorded.map((l) => l.productId));
  const tonnes = baseUomByProductId ? totalOutputWetTonnes(outputs, baseUomByProductId) : null;
  return recipe.consumables
    .filter((c) => !recordedIds.has(c.productId))
    .map((c) => ({
      productId: c.productId,
      qtyPerOutputTonne: c.qtyPerOutputTonne,
      // tonnes === 0 -> null, NOT "0.000": the server refuses to backflush
      // against a zero KG basis rather than silently under-booking (R6).
      estimatedQty: tonnes !== null && tonnes > 0 ? (Number(c.qtyPerOutputTonne) * tonnes).toFixed(3) : null,
    }));
}
